import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges, ViewChild, output } from "@angular/core";
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from "ngx-popperjs";
import { FormsModule } from "@angular/forms";
import { AsyncPipe, NgClass, NgFor, NgIf } from "@angular/common";
import { RibbonButtonComponent } from "../../../../shared/buttons/ribbon-button/ribbon-button.component";
import { MultiSelect, MultiSelectModule } from 'primeng/multiselect';
import { IMaskModule } from "angular-imask";
import { OverlayOptions } from "primeng/api";
import { PopperHelper } from "@nts/std/utility";
import { BehaviorSubject, map, Subject, takeUntil } from "rxjs";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { EnumItemInterface } from "../../../../../view-models/base-type/enum-property-view-model";
import { UICommandInterface } from "@nts/std/interfaces";


@UntilDestroy()
@Component({
  selector: 'nts-base-select-chips-box',
  templateUrl: './base-select-chips-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./base-select-chips-box.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    NgxPopperjsModule,
    NgClass,
    NgIf,
    RibbonButtonComponent,
    AsyncPipe,
    NgFor,
    MultiSelectModule,
    IMaskModule
  ]
})
export class BaseSelectChipsBoxComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() tabIndex = -1;
  @Input() isDisabled = false;
  @Input() value = [];
  @Input() isReadonly = false;
  @Input() placeholder = '';
  @Input() customClasses = '';
  @Input() errorList: string[] = [];
  @Input() showErrorTooltip = true;
  @Input() showErrorBorder = true;
  @Input() customCommandList: UICommandInterface[] = [];
  @Input() defaultBorderColor = null;
  @Input() activeBorderColor = null;
  @Input() hoverBorderColor = null;
  @Input() listenClickOutside = false;
  @Input() multiline = false;
  @Input() field = null;
  @Input() options$: BehaviorSubject<Array<EnumItemInterface<number | string>>>;
  @Input() showClear = true;
  @Input() optionLabel = 'description';
  @Input() optionValue = 'code';
  @Input() appendTo: string | HTMLDivElement = null;
  @Input() panelClass = 'dropdown-panel-select-chips-bottom';
  @Input() overlayOptions: OverlayOptions = null;
  @Input() maxSelectedLabels: number | null = null;

  @Output() onFinishEditing = new EventEmitter();
  @Output() onBlur = new EventEmitter();
  @Output() onFocus = new EventEmitter();
  @Output() onValueChange = new EventEmitter<Array<string | number>>();
  readonly viewReady = output<void>();


  isViewReady = false;
  @ViewChild(MultiSelect, { static: true }) selectChipsBox: MultiSelect;
  @ViewChild(NgxPopperjsDirective, { static: true }) popperError: NgxPopperjsDirective;

  documentClickListener: any;
  ngxPopperjsTriggers = NgxPopperjsTriggers;
  ngxPopperjsPlacements = NgxPopperjsPlacements;
  overrideBorderColor = null;
  isActive = false;
  isHover = false;
  mask = null;
  optionsDestroy$: Subject<void> = new Subject();
  simpleOptions$: BehaviorSubject<Array<object>> = new BehaviorSubject<Array<object>>([]);
  private destroyed = false;
  private viewInitTimeout?: ReturnType<typeof setTimeout>;

  constructor(
    public readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly cd: ChangeDetectorRef,
  ) {
  }
  ngAfterViewInit(): void {

    // this.updateChipsValues();
    this.cd.detectChanges();

    this.viewInitTimeout = setTimeout(() => {
      if (this.destroyed) {
        return;
      }
      // set tab index, current component multiSelect has a bug and doesn't set tabindex
      const selectChipsBox = this.selectChipsBox.el.nativeElement;
      selectChipsBox.setAttribute('tabindex', this.tabIndex);
    });

    queueMicrotask(() => {
      if (!this.destroyed) {
        this.emitViewReady();
      }
    });

  }

  // private updateChipsValues() {
  //     this.chipsBox.writeValue.call(this.chipsBox, this.value);
  //     this.chipsBox.value = this.value;
  //     this.chipsBox.onModelChange.call(this.chipsBox, (value) => this.valueChange(value));
  //     this.chipsBox.registerOnTouched.call(this.chipsBox, () => {
  //         this.value = this.chipsBox.value;
  //         this.valueChange(this.chipsBox.value);
  //         this.cd.detectChanges();
  //     });

  // }

  onModelChange(value: any): void {
    this.onValueChange.emit(value);
  }

  ngOnInit() {
    if (this.listenClickOutside) {
      this.bindDocumentClickListener();
    }
    this.handleOverridesColors();
    this.updateOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultBorderColor'] || changes['activeBorderColor'] || changes['hoverBorderColor']) {
      this.handleOverridesColors();
    }

    if (changes['errorList']) {
      this.checkPopper();
    }

    if (changes['value']) {
      // this.updateChipsValues();
      this.cd.detectChanges();
    }

    if (changes['options$']) {
      this.updateOptions();
    }
  }

  focus(e): void {
    this.isActive = true;
    this.handleOverridesColors();
    this.cd.detectChanges();
    this.onFocus.emit(e);
  }

  blur(e) {
    this.popperError?.hide();
    this.cd.detectChanges();
    this.onBlur.emit(e);
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.viewInitTimeout) {
      clearTimeout(this.viewInitTimeout);
      this.viewInitTimeout = undefined;
    }
    this.optionsDestroy$.next();
    this.optionsDestroy$.complete();
    this.unbindDocumentClickListener();
  }

  updateOptions() {
    this.optionsDestroy$.next();
    this.options$
      .pipe(
        takeUntil(this.optionsDestroy$),
        untilDestroyed(this),
        map((options) => {
          return options.map((singleOption: EnumItemInterface<number | string>) => {
            return {
              [this.optionValue]: singleOption.key,
              [this.optionLabel]: singleOption.description,
            };
          });
        })
      )
      .subscribe((s) => {
        this.simpleOptions$.next(s);
      });
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  mouseEnter(e) {
    this.isHover = true;
    this.handleOverridesColors();
  }

  mouseLeave(e) {
    this.isHover = false;
    this.handleOverridesColors();
  }

  handleOverridesColors() {
    if (!this.defaultBorderColor || !this.activeBorderColor || !this.hoverBorderColor) {
      // devono essere impostate tutte e tre le variabili
      return;
    }

    this.overrideBorderColor = this.defaultBorderColor;
    if (this.isActive && !this.isDisabled) {
      this.overrideBorderColor = this.activeBorderColor;
    }
    if (this.isHover && !this.isDisabled) {
      this.overrideBorderColor = this.hoverBorderColor;
    }
  }

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

        this.documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.zone.run(() => {
              this.onFinishEditing.emit();

              this.cd.markForCheck();
            });
          }

        });
      });
    }
  }

  isOutsideClicked(event: Event) {
    return !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target));
  }

  valueChange(value: Array<string | number>): void {
    this.onValueChange.emit(value);
  }

  private checkPopper() {
    if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
      PopperHelper.hide(this.popperError);
    }
    // if (this.popperInfo && this.errorList?.length > 0) {
    //     PopperHelper.hide(this.popperInfo);
    // }
  }

  private emitViewReady(): void {
    if (this.destroyed) {
      return;
    }
    this.isViewReady = true;
    this.viewReady.emit();
  }
}
